import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { WaterInfoImageType } from '@models/water-image-types';
import type {
  WaterContentBlockSaveRequest,
  WaterExpectationSaveRequest,
  WaterHotspotSaveRequest,
  WaterInformationDetail,
  WaterPhoto,
  WaterPriceLabelSaveRequest,
} from '@models/water-information';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CrmWaterInformationService } from '@services/crm-water-information.service';
import { CrmWatersService } from '@services/crm-waters.service';
import { ToastService } from '@services/toast.service';
import { BreadcrumbComponent, type BreadcrumbItem } from '@shared/components/breadcrumb/breadcrumb.component';
import { finalize, forkJoin, Observable, of, switchMap } from 'rxjs';
import { WaterInformationContentBlocksSectionComponent } from './sections/water-information-content-blocks-section.component';
import { WaterInformationGeneralSectionComponent } from './sections/water-information-general-section.component';
import { WaterInformationHotspotsSectionComponent } from './sections/water-information-hotspots-section.component';
import { WaterInformationLocationSectionComponent } from './sections/water-information-location-section.component';
import { WaterInformationPricingSectionComponent } from './sections/water-information-pricing-section.component';
import { WaterInformationSeoSectionComponent } from './sections/water-information-seo-section.component';
import { patchForms, toCollections, toImageStateMap, toSaveDto } from './water-information.mapper';
import type {
  GeneralForm,
  HotspotIntroductionForm,
  ImageState,
  LocationForm,
  PricingForm,
  SeoForm,
} from './water-information-form.types';

@Component({
  selector: 'app-water-information-form',
  templateUrl: './water-information-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTabsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    BreadcrumbComponent,
    WaterInformationGeneralSectionComponent,
    WaterInformationContentBlocksSectionComponent,
    WaterInformationPricingSectionComponent,
    WaterInformationHotspotsSectionComponent,
    WaterInformationLocationSectionComponent,
    WaterInformationSeoSectionComponent,
  ],
})
export class WaterInformationFormComponent implements OnInit {
  private readonly informationService = inject(CrmWaterInformationService);
  private readonly watersService = inject(CrmWatersService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly waterIdSig = signal(0);
  readonly isSaving = signal(false);
  readonly waterTitle = signal('');

  readonly breadcrumbItems = computed<BreadcrumbItem[]>(() => [
    { label: this.translate.instant('nav.waters'), routerLink: ['/waters'] },
    {
      label: this.waterTitle() || String(this.waterIdSig()),
      routerLink: ['/waters/edit', this.waterIdSig()],
      queryParams: { tab: 4 },
    },
    { label: this.translate.instant('waters.information.breadcrumb') },
  ]);

  readonly waterResource = rxResource({
    params: () => (this.waterIdSig() ? { waterId: this.waterIdSig() } : undefined),
    stream: ({ params }) => this.watersService.getWater(params.waterId),
  });

  readonly informationResource = rxResource({
    params: () => (this.waterIdSig() ? { waterId: this.waterIdSig() } : undefined),
    stream: ({ params }) => this.informationService.get(params.waterId),
  });

  readonly isLoading = this.informationResource.isLoading;

  readonly appHeader = signal<ImageState>({ name: null, url: null });
  readonly mapImage = signal<ImageState>({ name: null, url: null });
  readonly locationImage = signal<ImageState>({ name: null, url: null });
  readonly ambiancePoster = signal<ImageState>({ name: null, url: null });

  private readonly loadedInfoImages = signal<Partial<Record<WaterInfoImageType, ImageState>>>({});
  private readonly pendingInfoImages = signal<Map<WaterInfoImageType, File>>(new Map());
  private readonly pendingDeletedInfoImages = signal<Set<WaterInfoImageType>>(new Set());
  readonly pendingHotspotImages = signal<Map<string, File>>(new Map());
  readonly pendingBlockImages = signal<Map<string, File>>(new Map());

  private readonly imageSignals: Record<WaterInfoImageType, WritableSignal<ImageState>> = {
    [WaterInfoImageType.AppHeader]: this.appHeader,
    [WaterInfoImageType.Map]: this.mapImage,
    [WaterInfoImageType.Location]: this.locationImage,
    [WaterInfoImageType.AmbiancePoster]: this.ambiancePoster,
  };

  readonly photos = signal<WaterPhoto[]>([]);
  readonly expectations = signal<WaterExpectationSaveRequest[]>([]);
  readonly hotspots = signal<WaterHotspotSaveRequest[]>([]);
  readonly contentBlocks = signal<WaterContentBlockSaveRequest[]>([]);
  readonly priceLabels = signal<WaterPriceLabelSaveRequest[]>([]);

  readonly generalForm = new FormGroup<GeneralForm>({
    shortDescriptionNl: new FormControl(null),
    shortDescriptionEn: new FormControl(null),
    shortDescriptionDe: new FormControl(null),
    shortDescriptionIt: new FormControl(null),
    shortDescriptionFr: new FormControl(null),
    placeNl: new FormControl(null),
    placeEn: new FormControl(null),
    placeDe: new FormControl(null),
    placeIt: new FormControl(null),
    placeFr: new FormControl(null),
    ambianceVideoNl: new FormControl(null),
    ambianceVideoEn: new FormControl(null),
    ambianceVideoDe: new FormControl(null),
    ambianceVideoIt: new FormControl(null),
    ambianceVideoFr: new FormControl(null),
    uspsNl: new FormControl(null),
    uspsEn: new FormControl(null),
    uspsDe: new FormControl(null),
    uspsIt: new FormControl(null),
    uspsFr: new FormControl(null),
    rulesNl: new FormControl(null),
    rulesEn: new FormControl(null),
    rulesDe: new FormControl(null),
    rulesIt: new FormControl(null),
    rulesFr: new FormControl(null),
    infoPackNl: new FormControl(null),
    infoPackEn: new FormControl(null),
    infoPackDe: new FormControl(null),
    infoPackIt: new FormControl(null),
    infoPackFr: new FormControl(null),
  });

  readonly pricingForm = new FormGroup<PricingForm>({
    priceIntroductionNl: new FormControl(null),
    priceIntroductionEn: new FormControl(null),
    priceIntroductionDe: new FormControl(null),
    priceIntroductionIt: new FormControl(null),
    priceIntroductionFr: new FormControl(null),
    plainPriceSummaryNl: new FormControl(null),
    plainPriceSummaryEn: new FormControl(null),
    plainPriceSummaryDe: new FormControl(null),
    plainPriceSummaryIt: new FormControl(null),
    plainPriceSummaryFr: new FormControl(null),
    plainPriceServicesTextNl: new FormControl(null),
    plainPriceServicesTextEn: new FormControl(null),
    plainPriceServicesTextDe: new FormControl(null),
    plainPriceServicesTextIt: new FormControl(null),
    plainPriceServicesTextFr: new FormControl(null),
  });

  readonly locationForm = new FormGroup<LocationForm>({
    locationAddressNl: new FormControl(null),
    locationAddressEn: new FormControl(null),
    locationAddressDe: new FormControl(null),
    locationAddressIt: new FormControl(null),
    locationAddressFr: new FormControl(null),
    locationCoordinates: new FormControl(null),
  });

  readonly seoForm = new FormGroup<SeoForm>({
    metaDescriptionNl: new FormControl(null),
    metaDescriptionEn: new FormControl(null),
    metaDescriptionDe: new FormControl(null),
    metaDescriptionIt: new FormControl(null),
    metaDescriptionFr: new FormControl(null),
  });

  readonly hotspotIntroductionForm = new FormGroup<HotspotIntroductionForm>({
    nl: new FormControl(null),
    en: new FormControl(null),
    de: new FormControl(null),
    it: new FormControl(null),
    fr: new FormControl(null),
  });

  constructor() {
    effect(() => {
      const water = this.waterResource.value();
      if (water) this.waterTitle.set(water.title);
    });

    effect(() => {
      const info = this.informationResource.value();
      if (info) this.applyInfo(info);
    });
  }

  ngOnInit(): void {
    this.waterIdSig.set(Number(this.route.snapshot.paramMap.get('id')));
  }

  private get forms() {
    return {
      general: this.generalForm,
      pricing: this.pricingForm,
      location: this.locationForm,
      seo: this.seoForm,
      hotspotIntroduction: this.hotspotIntroductionForm,
    };
  }

  private revokeIfBlob(url: string | null | undefined): void {
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
  }

  private applyInfo(info: WaterInformationDetail): void {
    patchForms(this.forms, info);

    for (const sig of Object.values(this.imageSignals)) {
      this.revokeIfBlob(untracked(sig).url);
    }

    const images = toImageStateMap(info);
    this.loadedInfoImages.set({
      [WaterInfoImageType.AppHeader]: images.appHeader,
      [WaterInfoImageType.Map]: images.mapImage,
      [WaterInfoImageType.Location]: images.locationImage,
      [WaterInfoImageType.AmbiancePoster]: images.ambiancePoster,
    });
    this.appHeader.set(images.appHeader);
    this.mapImage.set(images.mapImage);
    this.locationImage.set(images.locationImage);
    this.ambiancePoster.set(images.ambiancePoster);

    const cols = toCollections(info);
    this.photos.set(cols.photos);
    this.expectations.set(cols.expectations);
    this.hotspots.set(cols.hotspots);
    this.contentBlocks.set(cols.contentBlocks);
    this.priceLabels.set(cols.priceLabels);

    this.pendingInfoImages.set(new Map());
    this.pendingDeletedInfoImages.set(new Set());
    this.pendingHotspotImages.set(new Map());
    this.pendingBlockImages.set(new Map());
  }

  selectInfoImage(type: WaterInfoImageType, file: File): void {
    this.revokeIfBlob(this.imageSignals[type]().url);
    this.pendingInfoImages.update((m) => new Map(m).set(type, file));
    this.pendingDeletedInfoImages.update((s) => {
      const n = new Set(s);
      n.delete(type);
      return n;
    });
    this.imageSignals[type].set({ name: file.name, url: URL.createObjectURL(file) });
  }

  clearInfoImage(type: WaterInfoImageType): void {
    const hasPending = this.pendingInfoImages().has(type);
    this.pendingInfoImages.update((m) => {
      const n = new Map(m);
      n.delete(type);
      return n;
    });
    if (hasPending) {
      this.revokeIfBlob(this.imageSignals[type]().url);
      const loaded = this.loadedInfoImages()[type];
      this.imageSignals[type].set(loaded ?? { name: null, url: null });
    } else {
      this.pendingDeletedInfoImages.update((s) => new Set(s).add(type));
      this.imageSignals[type].set({ name: null, url: null });
    }
  }

  onHotspotImageSelected(event: { key: string; file: File }): void {
    this.pendingHotspotImages.update((m) => new Map(m).set(event.key, event.file));
  }

  onHotspotImageCleared(key: string): void {
    this.pendingHotspotImages.update((m) => {
      const n = new Map(m);
      n.delete(key);
      return n;
    });
  }

  onBlockImageSelected(event: { key: string; file: File }): void {
    this.pendingBlockImages.update((m) => new Map(m).set(event.key, event.file));
  }

  onBlockImageCleared(key: string): void {
    this.pendingBlockImages.update((m) => {
      const n = new Map(m);
      n.delete(key);
      return n;
    });
  }

  save(): void {
    this.isSaving.set(true);
    const dto = toSaveDto(this.forms, {
      photos: this.photos(),
      expectations: this.expectations(),
      hotspots: this.hotspots(),
      contentBlocks: this.contentBlocks(),
      priceLabels: this.priceLabels(),
    });

    const waterId = this.waterIdSig();
    const pendingInfo = new Map(this.pendingInfoImages());
    const pendingDeleted = new Set(this.pendingDeletedInfoImages());
    const pendingHotspots = new Map(this.pendingHotspotImages());
    const pendingBlocks = new Map(this.pendingBlockImages());

    this.informationService
      .save(waterId, dto)
      .pipe(
        switchMap((savedInfo) => {
          const ops: Observable<unknown>[] = [];

          for (const type of pendingDeleted) {
            ops.push(this.informationService.deleteInfoImage(waterId, type));
          }
          for (const [type, file] of pendingInfo) {
            ops.push(this.informationService.uploadInfoImage(waterId, type, file));
          }
          for (const [key, file] of pendingHotspots) {
            const hotspot = savedInfo.hotspots.find((h) => String(h.id) === key || h.clientId === key);
            if (hotspot) ops.push(this.informationService.uploadHotspotImage(waterId, hotspot.id, file));
          }
          for (const [key, file] of pendingBlocks) {
            const block = savedInfo.contentBlocks.find((b) => String(b.id) === key || b.clientId === key);
            if (block) ops.push(this.informationService.uploadContentBlockImage(waterId, block.id, file));
          }

          return ops.length > 0 ? forkJoin(ops) : of(null);
        }),
        switchMap(() => this.informationService.get(waterId)),
        finalize(() => this.isSaving.set(false)),
      )
      .subscribe({
        next: (info) => {
          this.applyInfo(info);
          this.toastService.showSuccess(this.translate.instant('waters.information.save-success'));
        },
        error: () => this.toastService.showError(this.translate.instant('waters.information.save-failed')),
      });
  }

  cancel(): void {
    this.router.navigate(['/waters/edit', this.waterIdSig()], { queryParams: { tab: 4 } });
  }
}
